import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

export const toolKinds = ['http', 'mcp', 'code', 'builtin'] as const;
export const toolKindSchema = z.enum(toolKinds);
export type ToolKind = z.infer<typeof toolKindSchema>;

/**
 * A tool's input/output contract is expressed as a JSON Schema (object form).
 * We keep it as a permissive record here and validate concrete payloads at call time.
 */
export const jsonSchemaSchema: z.ZodType<Record<string, unknown>> = z
  .record(z.unknown())
  .refine((v) => typeof v === 'object' && v !== null, 'must be a JSON Schema object');

export const httpToolConfigSchema = z.object({
  kind: z.literal('http'),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).default('POST'),
  urlTemplate: z.string().url().or(z.string().min(1)),
  headers: z.record(z.string()).default({}),
  /** Reference to a secret in Vault (never the secret itself). */
  authSecretRef: z.string().optional(),
});

export const mcpToolConfigSchema = z.object({
  kind: z.literal('mcp'),
  serverId: idSchema,
  remoteToolName: z.string().min(1),
});

export const codeToolConfigSchema = z.object({
  kind: z.literal('code'),
  /** Entry module resolvable inside the sandbox. */
  entry: z.string().min(1),
  timeoutMs: z.number().int().min(100).max(120_000).default(15_000),
});

export const builtinToolConfigSchema = z.object({
  kind: z.literal('builtin'),
  handler: z.string().min(1),
  /** For coding sub-tools: the coding-agent endpoint name to call. */
  remoteToolName: z.string().optional(),
});

export const toolConfigSchema = z.discriminatedUnion('kind', [
  httpToolConfigSchema,
  mcpToolConfigSchema,
  codeToolConfigSchema,
  builtinToolConfigSchema,
]);
export type ToolConfig = z.infer<typeof toolConfigSchema>;

export const toolSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    key: z
      .string()
      .min(1)
      .max(120)
      .regex(/^[a-z0-9_.-]+$/, 'tool key must be kebab/snake case'),
    name: z.string().min(1).max(200),
    description: z.string().max(2000).default(''),
    kind: toolKindSchema,
    inputSchema: jsonSchemaSchema,
    outputSchema: jsonSchemaSchema.optional(),
    config: toolConfigSchema,
    /** Destructive tools require a human approval before execution. */
    requiresApproval: z.boolean().default(false),
    rateLimitPerMinute: z.number().int().min(0).default(60),
    enabled: z.boolean().default(true),
  })
  .merge(timestampsSchema);
export type Tool = z.infer<typeof toolSchema>;

export const createToolSchema = toolSchema.pick({
  key: true,
  name: true,
  description: true,
  kind: true,
  inputSchema: true,
  outputSchema: true,
  config: true,
  requiresApproval: true,
  rateLimitPerMinute: true,
  enabled: true,
}).extend({
  workspaceId: idSchema.optional(),
});
export type CreateTool = z.infer<typeof createToolSchema>;

export const updateToolSchema = createToolSchema.partial();
export type UpdateTool = z.infer<typeof updateToolSchema>;
